import { Avatar, AvatarFallback, cn } from '@hub/ui';
import type { AuditBatchState, CaseStatus, Coder } from '../types';
import { coderIdentity } from '../data/coders';

// Shared presentational atoms for the audit primitives (HUB-504). Built on
// @hub/ui + the design-system --ch-* tokens; no prototype CSS. Coder avatars keep
// the prototype's per-coder hue so a batch's staffing reads at a glance.

/** A coder avatar tinted with the coder's hue (ported from the prototype). */
export function CoderAvatar({ coder, size = 'sm' }: { coder: Coder; size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs';
  return (
    <Avatar className={cn(dim, 'ring-2 ring-background')} title={coder.name}>
      <AvatarFallback
        className="font-semibold text-white"
        style={{ backgroundColor: `hsl(${coder.hue} 52% 42%)` }}
      >
        {coder.initials}
      </AvatarFallback>
    </Avatar>
  );
}

/** How many faces fit before the stack stops being readable. Measured batches
 *  carry three to five coders, and the tail is long. */
const MAX_STACKED_CODERS = 4;

/**
 * Overlapping stack of coder avatars for a batch.
 *
 * Two faults this fixes, both of which made the column meaningless:
 *
 *  - Every avatar rendered as the same person. Resolution went through
 *    coderById, which fell back to the first demo coder on any miss, and the
 *    server sends FA's raw numeric ids — so a batch worked by five people
 *    showed one face five times. coderIdentity cannot resolve an unknown id to
 *    a real person.
 *  - Ids were rendered unbounded and undeduplicated, so a batch with thirty
 *    coders pushed the rest of the row off screen.
 */
export function CoderStack({
  coderIds,
  coderNames = [],
}: {
  coderIds: string[];
  /** Positionally aligned with coderIds and possibly SHORTER — an id FA could
   *  not resolve contributes none. Look up by INDEX into the original array,
   *  not by position in the deduplicated one. */
  coderNames?: string[];
}) {
  const unique = [...new Set(coderIds.filter(Boolean))];
  const shown = unique.slice(0, MAX_STACKED_CODERS);
  const overflow = unique.length - shown.length;
  return (
    <span className="flex items-center -space-x-1.5">
      {shown.map((id) => (
        <CoderAvatar key={id} coder={coderIdentity(id, coderNames[coderIds.indexOf(id)])} />
      ))}
      {overflow > 0 && (
        <span
          className="flex h-6 items-center rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground ring-2 ring-background"
          title={`${unique.length} coders worked this batch`}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}

/** How a batch's coder staffing reads in one line beside the stack. */
export function coderStackLabel(coderIds: string[], coderNames: string[] = []): string {
  const unique = [...new Set(coderIds.filter(Boolean))];
  if (unique.length === 0) return 'No coder recorded';
  if (unique.length === 1) {
    // Names are positionally aligned with ids and MAY be shorter — an id that
    // FA could not resolve contributes none. Index, never zip.
    const at = coderIds.indexOf(unique[0]);
    return coderIdentity(unique[0], coderNames[at]).name;
  }
  return `${unique.length} coders`;
}

/** A thin progress bar. `tone` matches the design: teal when complete, amber in
 *  progress. */
export function Progress({ value, max, tone }: { value: number; max: number; tone: 'teal' | 'amber' }) {
  const pct = max > 0 ? Math.round((Math.max(0, Math.min(value, max)) / max) * 100) : 0;
  const barColor = tone === 'teal' ? 'var(--ch-teal-500)' : 'var(--ch-amber-500)';
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemax={max}
      aria-valuemin={0}
    >
      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
    </div>
  );
}

/** Status-pill tone. Backed by theme-aware `--pill-{tone}-*` tokens (@hub/config
 *  design-system.css) so a pill reads correctly in light / off-white / dark. */
type PillTone = 'blue' | 'amber' | 'teal' | 'slate';

/**
 * A tone-tinted status pill (dot + label) matching the design-system status-pill
 * spec: tinted bg + 1px border + 11px label + a small status dot. `whitespace-nowrap`
 * and a fixed height keep 1- and 2-word labels ("Complete" vs "In progress") on a
 * single line at a consistent size — never wrapping into an oversized oval. Tones
 * resolve through `--pill-*` tokens, so no washes glare on the dark navy surface. */
export function StatusPill({ tone, label }: { tone: PillTone; label: string }) {
  return (
    <span
      className="inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-[11px] font-semibold leading-none"
      style={{
        backgroundColor: `var(--pill-${tone}-bg)`,
        color: `var(--pill-${tone}-fg)`,
        borderColor: `var(--pill-${tone}-border)`,
      }}
    >
      <span
        className="size-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: `var(--pill-${tone}-dot)` }}
        aria-hidden
      />
      {label}
    </span>
  );
}

const BATCH_STATE_META: Record<AuditBatchState, { label: string; tone: PillTone }> = {
  new: { label: 'Not started', tone: 'blue' },
  active: { label: 'In progress', tone: 'amber' },
  complete: { label: 'Complete', tone: 'teal' },
};

export function batchStateLabel(state: AuditBatchState): string {
  return BATCH_STATE_META[state].label;
}

/** Batch lifecycle pill (new / active / complete). */
export function BatchStateBadge({ state }: { state: AuditBatchState }) {
  const meta = BATCH_STATE_META[state];
  return <StatusPill tone={meta.tone} label={meta.label} />;
}

/**
 * FA's own batch status codes.
 *
 * Established from FirstClaim's source rather than guessed: CodingDAO sets 'P'
 * when coding opens, RefundBatchAction and BalanceBillerConstants both define
 * 'C' as closed, and 'D'/'F' appear as deleted and frozen across the DAOs.
 *
 * The pill used to render the bare letter, with a note that the meanings were
 * unconfirmed. They are confirmed now — but the raw code stays in the tooltip,
 * because that caution was well founded: FA status columns have been misread
 * before (BATCH_CD_STATUS_CODE reads 'N' on batches demonstrably coded).
 */
const FA_STATUS_LABELS: Record<string, string> = {
  c: 'Closed',
  p: 'Pending',
  d: 'Deleted',
  f: 'Frozen',
};

/** FA's own batch status, shown beside the lifecycle pill.
 *
 *  The lifecycle pill is derived from OUR review progress, so before anyone
 *  reviews it reads "Not started" on every batch — a column identical on every
 *  row tells an auditor nothing. FA's BATCH_STATUS is the only batch column
 *  measured to vary ('C' 87.7% / 'P' 12.3% across 277 sampled batches).
 *
 *  PRIORITY, HOLD_CODE and BATCH_TYPE were measured constant over the same
 *  sample and stay absent: a chip identical on every row is the fault this is
 *  fixing.
 *
 *  Beside the pill rather than as a new column — the batch table already
 *  overflows horizontally, which is how the counts got pushed out of view. */
export function BatchFlags({ faStatus }: { faStatus?: string }) {
  const code = faStatus?.trim();
  if (!code) return null;
  const label = FA_STATUS_LABELS[code.toLowerCase()];
  return (
    <span title={`FirstAnesthesia BATCH_STATUS ${code}`}>
      {/* Prefixed either way, so nobody reads it as one of OUR statuses. */}
      <StatusPill tone="slate" label={label ? `FA ${label}` : `FA ${code}`} />
    </span>
  );
}

/** Per-case status pill (submitted / unreviewed) — matches the batch pills. */
export function CaseStatusBadge({ status }: { status: CaseStatus }) {
  return status === 'submitted' ? (
    <StatusPill tone="teal" label="Submitted" />
  ) : (
    <StatusPill tone="slate" label="Unreviewed" />
  );
}

/** The call-to-action verb for a batch given its state. */
export function batchActionLabel(state: AuditBatchState): string {
  if (state === 'complete') return 'View';
  if (state === 'active') return 'Continue';
  return 'Start';
}

/** The batch number rendered as the primary batch identifier — JetBrains Mono
 *  (it reads as a code/id), tight tracking. `className` sets the size/weight at
 *  each call site (card heading, table cell, detail header). */
export function BatchNumber({ value, className }: { value: string; className?: string }) {
  return <span className={cn('font-mono tracking-tight text-foreground', className)}>{value}</span>;
}

/** A monospaced 4-char job-code chip. */
export function JobCodeChip({ code }: { code: string }) {
  return (
    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
      {code}
    </span>
  );
}

/**
 * A case's identifier: FA's episode id, or the DCN with a tag saying so.
 *
 * # Why the tag
 *
 * The identifier is the episode id where FA has claimed the case and the DCN
 * where it has not (see data/caseIdentity). In PRODUCTION both are bare digit
 * strings — an episode id runs to about eight digits, a DCN to fifteen — so a
 * column that silently swaps between them gives a reviewer two numbers they can
 * only tell apart by counting, on the screen whose whole purpose is letting them
 * cross-reference a case back into FA. Quoting the wrong one to FA finds
 * nothing, or worse finds someone else: a DCN is scoped per job and repeats
 * across clients (HUB-633).
 *
 * So the fallback is LABELLED. The tag appears only on the fallback, because
 * tagging every row would make the common case noisier to say something a
 * reviewer already knows.
 *
 * The tooltip carries both numbers either way, so the one that is not on screen
 * is always a hover away.
 */
export function CaseIdentifier({
  value,
  isFallback,
  title,
  className,
}: {
  value: string;
  /** True when `value` is the DCN standing in for an absent episode id. */
  isFallback: boolean;
  title: string;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex items-baseline gap-1.5', className)} title={title}>
      <span className="font-mono tracking-tight">{value}</span>
      {isFallback && <FallbackTag>DCN</FallbackTag>}
    </span>
  );
}

/** The muted marker that says a value is a FALLBACK — a different kind of thing
 *  from the one the field normally holds. Shared so the DCN standing in for an
 *  episode id and a facility id standing in for a facility name read the same,
 *  because they are the same failure to guard against: two unlike values
 *  swapped silently in one field. */
function FallbackTag({ children }: { children: string }) {
  return (
    <span className="shrink-0 rounded bg-muted px-1 py-px font-mono text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </span>
  );
}

/**
 * A facility: FA's name for it, or its id marked as an id.
 *
 * FACILITY.FACILITY_NAME is nullable in FA, so an episode can carry a real
 * facility id and no name (HUB-786). Falling back silently would put "71796"
 * and "Houston Methodist" in one column with nothing saying which a reader is
 * looking at — the same trap the DCN fallback has, so it gets the same tag.
 */
export function FacilityLabel({
  facilityId,
  facilityName,
  className,
}: {
  facilityId: string;
  facilityName: string;
  className?: string;
}) {
  if (!facilityName && !facilityId) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (facilityName) {
    return (
      <span className={className} title={`FA facility ${facilityId}`}>
        {facilityName}
      </span>
    );
  }
  return (
    <span
      className={cn('inline-flex items-baseline gap-1.5', className)}
      title={`FA facility ${facilityId} — no name on file`}
    >
      <span className="font-mono tracking-tight">{facilityId}</span>
      <FallbackTag>ID</FallbackTag>
    </span>
  );
}
